import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ThemeProvider } from 'styled-components'
import { lightTheme, darkTheme } from '../theme'

const STORAGE_KEY = 'tartis-theme-mode'

const getSystemPrefersDark = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches

const readStoredMode = () => {
  if (typeof window === 'undefined') return null
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return stored === 'light' || stored === 'dark' ? stored : null
}

const ThemeModeContext = createContext({ mode: 'light', toggleMode: () => {} })

export const useThemeMode = () => useContext(ThemeModeContext)

export const ThemeModeProvider = ({ children }) => {
  const [override, setOverride] = useState(readStoredMode)
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark)

  useEffect(() => {
    if (!window.matchMedia) return undefined
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (event) => setSystemPrefersDark(event.matches)

    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange)
      return () => mql.removeEventListener('change', handleChange)
    }
    mql.addListener(handleChange)
    return () => mql.removeListener(handleChange)
  }, [])

  const mode = override || (systemPrefersDark ? 'dark' : 'light')

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode)
  }, [mode])

  const toggleMode = useCallback(() => {
    const next = mode === 'dark' ? 'light' : 'dark'
    setOverride(next)
    window.localStorage.setItem(STORAGE_KEY, next)
  }, [mode])

  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode])
  const activeTheme = mode === 'dark' ? darkTheme : lightTheme

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={activeTheme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  )
}
